import { defineStore } from 'pinia'
import { ref } from 'vue'

export interface UndoableItem {
  id: string | number
  label: string
  resourceType: string
  restoreAction: () => Promise<void> | void
}

export type UndoRestoredPayload = {
  resourceType: string
}

export const useUndoStore = defineStore('undo', () => {
  const item = ref<UndoableItem | null>(null)
  const isRestoring = ref(false)
  const restored = ref<UndoRestoredPayload | null>(null)
  let timer: ReturnType<typeof setTimeout> | null = null

  // Stores a recently deleted item and auto-clears after 30 seconds.
  const register = (
    resourceType: string,
    label: string,
    restoreAction: () => Promise<void> | void
  ) => {
    item.value = {
      id: Math.random().toString(),
      label,
      resourceType,
      restoreAction
    }

    if (timer) clearTimeout(timer)

    timer = setTimeout(() => {
      item.value = null
    }, 30000)
  }

  // Dismisses the undo banner and cancels the auto-clear timer.
  const clear = () => {
    item.value = null
    restored.value = null
    if (timer) clearTimeout(timer)
  }

  // Runs the registered restore action (e.g. restore from trash).
  const executeUndo = async () => {
    if (!item.value) return
    const resourceType = item.value.resourceType
    try {
      isRestoring.value = true
      await item.value.restoreAction()
      clear()
      restored.value = { resourceType }
    } catch (error) {
      console.error(`Failed to undo deletion of ${resourceType}:`, error)
    } finally {
      isRestoring.value = false
    }
  }

  const clearRestored = () => {
    restored.value = null
  }

  return {
    item,
    isRestoring,
    restored,
    register,
    clear,
    executeUndo,
    clearRestored
  }
})
